if (!customElements.get('cart-recommendations')) {
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.cart-recommendations__grid');
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));

    // Initial load from server-rendered cart item IDs
    const ids = (this.dataset.cartItems || '').split(',').filter(Boolean).map(Number);
    if (ids.length > 0) {
      this.cartItemIds = ids;
      this.load(ids[0]);
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  onCartUpdate() {
    // Fetch fresh cart state then reload recommendations
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((res) => res.json())
      .then((cart) => {
        if (!cart.items || cart.items.length === 0) {
          this.hidden = true;
          return;
        }
        this.cartItemIds = cart.items.map((i) => i.product_id);
        this.load(this.cartItemIds[0]);
      })
      .catch(() => {
        this.hidden = true;
      });
  }

  load(productId) {
    if (!productId) return;

    fetch(
      `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=6&intent=related`
    )
      .then((res) => res.json())
      .then(({ products }) => {
        const filtered = products.filter((p) => !this.cartItemIds.includes(p.id)).slice(0, 2);

        if (filtered.length === 0) {
          this.hidden = true;
          return;
        }

        this.grid.innerHTML = filtered.map((p) => this.renderCard(p)).join('');
        this.hidden = false;
      })
      .catch(() => {
        this.hidden = true;
      });
  }

  renderCard(product) {
    const image = product.featured_image
      ? `<img
           src="${product.featured_image}"
           alt="${this.escapeHtml(product.title)}"
           loading="lazy"
           width="300"
           height="300"
         >`
      : '';

    const price = product.price_varies
      ? `From ${this.formatMoney(product.price_min)}`
      : this.formatMoney(product.price);

    const comparePrice =
      product.compare_at_price_max > product.price
        ? `<s class="price-item price-item--regular">${this.formatMoney(product.compare_at_price_max)}</s>`
        : '';

    return `
      <div class="cart-rec-card">
        <a href="${product.url}" class="cart-rec-card__image-link" tabindex="-1" aria-hidden="true">
          <div class="cart-rec-card__image-wrapper">
            ${image}
          </div>
        </a>
        <div class="cart-rec-card__info">
          <a href="${product.url}" class="cart-rec-card__title full-unstyled-link">
            ${this.escapeHtml(product.title)}
          </a>
          <div class="cart-rec-card__price price">
            ${comparePrice}
            <span class="price-item price-item--sale">${price}</span>
          </div>
          ${this.renderQuickAdd(product)}
        </div>
      </div>
    `;
  }

  renderQuickAdd(product) {
    if (!product.available) {
      return `<p class="cart-rec-card__sold-out">Sold out</p>`;
    }

    if (!product.variants || product.variants.length === 1) {
      return `
        <button
          type="button"
          class="cart-rec-card__add button button--full-width button--secondary"
          data-variant-id="${product.variants[0].id}"
          aria-label="Add ${this.escapeHtml(product.title)} to cart"
        >
          Add
        </button>
      `;
    }

    return `
      <a href="${product.url}" class="cart-rec-card__add button button--full-width button--secondary">
        View options
      </a>
    `;
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.themeMoneyFormat || '${{amount}}');
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('cart-recommendations', CartRecommendations);

class CartRecommendationsPage extends HTMLElement {
  connectedCallback() {
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    this.mq = window.matchMedia('(max-width: 749px)');
    this.syncSlider = this.syncSlider.bind(this);
    this.mq.addEventListener('change', this.syncSlider);

    // Visibility breakpoint: the standalone section and in-summary copy
    // swap visibility at 1100px. When this instance becomes visible mid-session
    this.visibilityMq = window.matchMedia('(min-width: 1100px)');
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.visibilityMq.addEventListener('change', this.onVisibilityChange);

    if (this.isHidden()) return;
    const productId = this.dataset.productId;
    if (productId) this.load(productId);
  }

  onVisibilityChange() {
    if (this.isHidden()) return;
    if (this.innerHTML.trim()) return;
    const productId = this.dataset.productId;
    if (productId) this.load(productId);
  }

  isHidden() {
    // Don't fetch if CSS has us hidden at this breakpoint — both render
    // locations (standalone section + in-summary) exist in DOM at all times,
    // and we don't want both firing requests on every cart update.
    return getComputedStyle(this).display === 'none';
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.mq) this.mq.removeEventListener('change', this.syncSlider);
    if (this.visibilityMq) this.visibilityMq.removeEventListener('change', this.onVisibilityChange);
    this.destroySlider();
  }

  onCartUpdate() {
    if (this.isHidden()) return;
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((res) => res.json())
      .then((cart) => {
        if (!cart.items || cart.items.length === 0) {
          this.destroySlider();
          this.innerHTML = '';
          return;
        }
        this.load(cart.items[0].product_id);
      })
      .catch(() => {
        this.destroySlider();
        this.innerHTML = '';
      });
  }

  load(productId) {
    const url = `${window.Shopify.routes.root}recommendations/products?section_id=cart-recommendations&product_id=${productId}&intent=related&limit=6`;
    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('cart-recommendations-page');
        this.destroySlider();
        this.innerHTML = fresh && fresh.innerHTML.trim() ? fresh.innerHTML : '';
        this.syncSlider();
      })
      .catch(() => {
        this.destroySlider();
        this.innerHTML = '';
      });
  }

  syncSlider() {
    const grid = this.querySelector('.cart-recommendations__grid');
    if (!grid) {
      this.destroySlider();
      return;
    }

    if (this.mq.matches) {
      if (this.slider) return;
      if (typeof window.KeenSlider === 'undefined') {
        // Library not loaded yet — retry next frame.
        requestAnimationFrame(() => this.syncSlider());
        return;
      }
      const slides = grid.querySelectorAll('.keen-slider__slide');
      if (slides.length < 2) return;
      this.slider = new window.KeenSlider(grid, {
        loop: false,
        renderMode: 'performance',
        slides: { perView: 1.15, spacing: 12 },
      });
    } else {
      this.destroySlider();
    }
  }

  destroySlider() {
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }
  }
}

customElements.define('cart-recommendations-page', CartRecommendationsPage);

// Handle "Add" button clicks within recommendation cards
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.cart-rec-card__add[data-variant-id]');
  if (!btn) return;

  btn.setAttribute('disabled', '');
  const originalText = btn.textContent.trim();
  btn.textContent = '...';

  fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
    body: JSON.stringify({ id: Number(btn.dataset.variantId), quantity: 1 }),
  })
    .then((res) => res.json())
    .then(() => {
      btn.textContent = 'Added!';
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recommendations' });
      setTimeout(() => {
        btn.textContent = originalText;
        btn.removeAttribute('disabled');
      }, 1500);
    })
    .catch(() => {
      btn.textContent = 'Error';
      btn.removeAttribute('disabled');
    });
});
}
